import React from "react";
import Modal from "components/modal";
import { api } from "utils/api";
import { formatarData, formatarMoeda } from "../utils/formato";

const PLANOS = [
  { value: "basico", label: "Básico" },
  { value: "intermediario", label: "Intermediário" },
  { value: "equipe", label: "Equipe" },
  { value: "free", label: "Free" },
];

export default function DetalheEmpresaModal({ empresaId, aberto, onFechar, onAlterado }) {
  const [carregando, setCarregando] = React.useState(true);
  const [dados, setDados] = React.useState(null);
  const [planoSelecionado, setPlanoSelecionado] = React.useState("");
  const [dias, setDias] = React.useState(7);
  const [executando, setExecutando] = React.useState(false);

  const carregar = React.useCallback(async () => {
    if (!empresaId) return;
    setCarregando(true);
    const data = await api(`/api/admin/empresas/${empresaId}`);
    if (data && data.success) {
      setDados(data);
      setPlanoSelecionado(data.empresa.plano);
    }
    setCarregando(false);
  }, [empresaId]);

  React.useEffect(() => {
    if (aberto) carregar();
  }, [aberto, carregar]);

  async function executarAcao(acao, corpo = {}) {
    setExecutando(true);
    try {
      const data = await api(`/api/admin/empresas/${empresaId}/${acao}`, {
        method: "POST",
        body: JSON.stringify(corpo),
      });
      if (!data) return;
      if (!data.success) {
        alert(data.error || "Não foi possível concluir.");
        return;
      }
      await carregar();
      onAlterado();
    } finally {
      setExecutando(false);
    }
  }

  return (
    <Modal aberto={aberto} onFechar={onFechar} titulo={dados?.empresa?.nome_fantasia || "Carregando..."} largura="max-w-4xl">
      {carregando || !dados ? (
        <p className="text-gray-500">Carregando...</p>
      ) : (
        <ConteudoDetalhe
          dados={dados}
          planoSelecionado={planoSelecionado}
          setPlanoSelecionado={setPlanoSelecionado}
          dias={dias}
          setDias={setDias}
          executando={executando}
          executarAcao={executarAcao}
        />
      )}
    </Modal>
  );
}

function ConteudoDetalhe({ dados, planoSelecionado, setPlanoSelecionado, dias, setDias, executando, executarAcao }) {
  const e = dados.empresa;
  const bloqueada = e.status_assinatura === "cancelada";

  return (
    <div className="flex flex-col gap-5">
      <div className="grid grid-cols-1 gap-2 text-sm text-navy-700 dark:text-white sm:grid-cols-2">
        <p><strong>Slug:</strong> {e.slug}</p>
        <p><strong>E-mail:</strong> {e.email || "—"}</p>
        <p><strong>Telefone:</strong> {e.telefone || "—"}</p>
        <p><strong>Documento:</strong> {e.documento || "—"}</p>
        <p><strong>Equipe ativa:</strong> {dados.totalEquipe}</p>
        <p><strong>Clientes cadastrados:</strong> {dados.totalClientes ?? "—"}</p>
        <p><strong>Trial iniciado em:</strong> {formatarData(e.data_inicio_trial)}</p>
        <p><strong>Vencimento atual:</strong> {formatarData(e.data_vencimento)}</p>
        {bloqueada && (
          <p className="col-span-2 font-semibold text-red-500">Bloqueada: status marcado como cancelada.</p>
        )}
      </div>

      <div className="h-px w-full bg-gray-200 dark:bg-white/10" />

      <div>
        <h5 className="mb-3 text-base font-bold text-navy-700 dark:text-white">Ações administrativas</h5>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="flex gap-2">
            <select
              className="h-10 flex-1 rounded-xl border border-gray-200 bg-white/0 px-3 text-sm dark:!border-white/10 dark:text-white"
              value={planoSelecionado}
              onChange={(ev) => setPlanoSelecionado(ev.target.value)}
            >
              {PLANOS.map((p) => (
                <option key={p.value} value={p.value}>{p.label}</option>
              ))}
            </select>
            <BotaoAcao disabled={executando} onClick={() => executarAcao("plano", { plano: planoSelecionado })}>
              Mudar plano
            </BotaoAcao>
          </div>

          <div className="flex gap-2">
            <input
              type="number"
              min="1"
              className="h-10 w-24 rounded-xl border border-gray-200 bg-white/0 px-3 text-sm dark:!border-white/10 dark:text-white"
              value={dias}
              onChange={(ev) => setDias(ev.target.value)}
            />
            <BotaoAcao disabled={executando} onClick={() => executarAcao("estender-trial", { dias: Number(dias) })}>
              Estender trial
            </BotaoAcao>
          </div>

          <BotaoAcao
            disabled={executando}
            onClick={() => {
              if (window.confirm("Marcar esta empresa como parceira (free), sem cobrança?")) executarAcao("marcar-free");
            }}
          >
            Marcar como free (parceiro)
          </BotaoAcao>

          {bloqueada ? (
            <BotaoAcao
              variante="sucesso"
              disabled={executando}
              onClick={() => {
                if (window.confirm("Reativar esta empresa com vencimento em 30 dias a partir de hoje?")) executarAcao("desbloquear");
              }}
            >
              Reativar (dá +30 dias de vencimento)
            </BotaoAcao>
          ) : (
            <BotaoAcao
              variante="perigo"
              disabled={executando}
              onClick={() => {
                if (window.confirm("Bloquear/cancelar a assinatura desta empresa? O acesso dela será travado no app.")) executarAcao("bloquear");
              }}
            >
              Bloquear / cancelar assinatura
            </BotaoAcao>
          )}
        </div>
        <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
          Bloquear e cancelar usam o mesmo status ("cancelada") — não há hoje, no banco, uma diferença entre
          "bloqueado pelo suporte" e "assinatura cancelada". Planos free/parceiro nunca são bloqueados por aqui
          (o app principal os isenta sempre); troque o plano antes, se precisar.
        </p>
      </div>

      <div className="h-px w-full bg-gray-200 dark:bg-white/10" />

      <div>
        <h5 className="mb-3 text-base font-bold text-navy-700 dark:text-white">Histórico de pagamentos (Asaas)</h5>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-gray-500 dark:text-gray-400">
                <th className="pb-2">Vencimento</th>
                <th className="pb-2">Valor</th>
                <th className="pb-2">Status</th>
                <th className="pb-2">Pago em</th>
                <th className="pb-2"></th>
              </tr>
            </thead>
            <tbody>
              {dados.pagamentos.length ? (
                dados.pagamentos.map((p) => (
                  <tr key={p.id} className="border-t border-gray-100 dark:border-white/5">
                    <td className="py-2">{formatarData(p.vencimento)}</td>
                    <td className="py-2">{formatarMoeda(p.valor)}</td>
                    <td className="py-2">{p.status}</td>
                    <td className="py-2">{p.pagoEm ? formatarData(p.pagoEm) : "—"}</td>
                    <td className="py-2">
                      {p.faturaUrl && (
                        <a href={p.faturaUrl} target="_blank" rel="noreferrer" className="text-brand-500 hover:underline">
                          ver fatura
                        </a>
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={5} className="py-4 text-center text-gray-500">
                    {dados.avisoPagamentos || "Sem pagamentos registrados."}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function BotaoAcao({ children, onClick, disabled, variante }) {
  const cores =
    variante === "perigo"
      ? "border-red-400 text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10"
      : variante === "sucesso"
      ? "border-green-400 text-green-600 hover:bg-green-50 dark:hover:bg-green-500/10"
      : "border-gray-300 text-navy-700 hover:bg-gray-50 dark:border-white/10 dark:text-white dark:hover:bg-white/5";

  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={`flex-1 rounded-xl border px-3 py-2 text-sm font-medium transition disabled:opacity-50 ${cores}`}
    >
      {children}
    </button>
  );
}
